import React, { useState } from "react";
import { DateTime } from "luxon";
import { getClassName } from "@utils";
import { DateRangeDefaults } from "@store/signals";
import { Button, ButtonSize, ButtonVariant, DatePicker, SelectOption } from "@taskpane/components";
import { CheckedIcon } from "@taskpane/icons";
import css from "./DateSelectContent.module.scss";

export interface DateSelectContentProps {
  options: SelectOption<string>[];
  selectedValue: string | null;
  handleChange: (value: string, shouldClose: boolean) => void;
}

const DateSelectContent: React.FC<DateSelectContentProps> = ({
  options,
  selectedValue,
  handleChange,
}) => {
  const isCustomValue = selectedValue?.includes("custom");
  const [fromDateString = null, toDateString = null] = selectedValue?.split("|") || [];

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [date, setDate] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: fromDateString && isCustomValue ? new Date(fromDateString).toISOString() : null,
    endDate: toDateString && isCustomValue ? new Date(toDateString).toISOString() : null,
  });

  const customValueToApply =
    isCustomValue && date.startDate && date.endDate
      ? `${DateTime.fromISO(date.startDate).toFormat("yyyy-MM-dd")}|${DateTime.fromISO(date.endDate).toFormat("yyyy-MM-dd")}|custom`
      : null;

  const attrs = {
    container: {
      className: getClassName(css.container, showDatePicker && css.containerWithDatePicker),
    },
    datePicker: {
      startDate: date.startDate,
      endDate: date.endDate,
      onChange: (startDate: string | null, endDate: string | null) => {
        setDate(() => ({ startDate, endDate }));
      },
    },
    buttonsWrapper: {
      className: css.buttonsWrapper,
    },
    clearButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Secondary,
      disabled: !(date.startDate || date.endDate),
      onClick: () => {
        setDate(() => ({ startDate: null, endDate: null }));
      },
    },
    applyButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Default,
      disabled: !customValueToApply || customValueToApply === selectedValue,
      onClick: () => {
        if (customValueToApply) {
          handleChange(customValueToApply, true);
          setShowDatePicker(false);
        }
      },
    },
  };

  const renderOptions = () =>
    options.map(({ value, label, dropdownLabel }, index) => {
      const isSelected = value === selectedValue;
      const isCustomValue = value.includes("custom");
      const attrs = {
        option: {
          type: "button" as const,
          className: css.option,
          disabled: isSelected && !isCustomValue,
          onClick: () => {
            handleChange(value, !isCustomValue);
            setShowDatePicker(isCustomValue);
          },
        },
      };

      return (
        <button key={index} {...attrs.option}>
          <span>{dropdownLabel || label}</span>
          {isSelected ? <CheckedIcon /> : null}
        </button>
      );
    });

  return (
    <div {...attrs.container}>
      {showDatePicker ? (
        <>
          <DatePicker {...attrs.datePicker} />
          <div {...attrs.buttonsWrapper}>
            <Button {...attrs.clearButton}>
              <span>Clear</span>
            </Button>
            <Button {...attrs.applyButton}>
              <span>Apply</span>
            </Button>
          </div>
        </>
      ) : (
        renderOptions()
      )}
    </div>
  );
};

export default DateSelectContent;
